<template>
  <div
    class="sf-bg-white sf-full-height tw-overflow-auto"
    data-testid="sf-insights-left-panel"
  >
    <div class="sf-layer-border-bottom">
      <div
        class="
          tw-py-3
          sf-pad-h-2
          tw-flex tw-gap-4 tw-w-full
          sf-layer-border-bottom
          tw-mt-[-1px]
        "
      >
        <sf-button-icon icon="arrow-left" @click="closeDrawer" />
        <div
          class="
            tw-w-full
            sf-btn sf-level sf-level-around
            rs-select-segment
            sf-c-primary
          "
          @click="showSegmentModal"
        >
          <div class="sf-level sf-level-around">
            <div
              class="sf-level"
              data-walkthrough-id="all-preset-queries"
              data-testid="see-all-preset-queries-btn"
            >
              {{ I18n.t('companies_overview.see_all_preset_queries') }}
            </div>
          </div>
        </div>
      </div>

      <div class="sf-pad-h-2">
        <segment-info v-if="activeFilters.length && !getError"></segment-info>

        <segment-selector />

        <div
          v-show="activeFilters.length"
          class="sf-pad-2 sf-bg-snow sf-below-1 rs-filter-list"
        >
          <div class="sf-uppercase sf-below-1">
            {{ I18n.t('companies_overview.filters_applied') }}
          </div>
          <active-filters-list :edit-filter="editFilter"></active-filters-list>
        </div>
      </div>
    </div>
    <div class="sf-pad-v-3 sf-pad-h-3 sf-layer-border-bottom sf-below-2">
      <div class="sf-title sf-font-l sf-below-1">
        {{ I18n.t('companies_overview.label_period_selection') }}
      </div>
      <template v-if="periodSelection">
        <div
          v-dompurify-html="
            I18n.t(
              'companies_overview.period_selection.short.' + periodSelection,
              { date: ledgerSelectDate || today }
            )
          "
          class="sf-pad-b-2"
          data-testid="segmentSelectedPeriod"
        />
      </template>
      <div
        class="
          sf-btn
          rs-open-period-selection
          sf-level sf-level-around sf-c-primary
        "
        data-walkthrough-id="select-period"
        @click="openPeriodSelection"
      >
        <div>{{ I18n.t('companies_overview.select_period') }}</div>
      </div>
    </div>

    <template v-if="segment && manageTrigger">
      <div class="sf-pad-3 sf-pad-t-1 sf-layer-border-bottom">
        <div class="sf-title sf-font-l sf-below-1">
          {{ I18n.t('companies_overview.triggers_new') }}
          <div
            v-if="triggerLoading"
            class="sf-spinner-wrapper sf-pad-l-1 sf-inline-block"
          >
            <div class="sf-spinner"></div>
          </div>
        </div>
        <template v-if="showTriggers">
          <div class="sf-pad-t-2 sf-below-3 rs-triggers">
            <div
              v-for="trigger in queryTriggers"
              :key="trigger.id"
              class="sf-level sf-below-2 sf-pointer"
              @click="setEditedTrigger(trigger)"
            >
              <div>
                <div v-if="trigger.running" class="sf-spinner"></div>
                <div
                  v-if="!trigger.running"
                  class="fa fa-paper-plane sf-c-primary"
                ></div>
              </div>
              <div
                :class="{ 'sf-disabled': triggerLoading }"
                class="
                  sf-c-text-dark sf-fwdb sf-font-m sf-pointer sf-dim-el-hover
                  rs-trigger-title
                "
              >
                {{ trigger.title }}
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-if="!triggerLoading"
            class="sf-text sf-c-text-light sf-pad-r-4 sf-pad-b-2"
          >
            {{ I18n.t('companies_overview.no_triggers_yet_new') }}
          </div>
        </template>
        <a
          v-if="addTriggerAllowed"
          :class="{ 'sf-disabled': isSegmentLoading }"
          class="sf-btn sf-level sf-level-around sf-c-primary rs-add-trigger"
          @click="addTrigger"
          >{{ I18n.t('companies_overview.add_trigger') }}</a
        >
      </div>
    </template>
    <div v-show="allFilters.length" class="sf-pad-h-3 sf-pad-v-2">
      <div class="sf-title sf-font-l sf-below-1">
        {{ I18n.t('companies_overview.filters.add_filters') }}
      </div>
      <div class="sf-font-1 sf-below-2 sf-c-text-darker">
        {{ I18n.t('companies_overview.narrow_down_your_client_list') }}
      </div>

      <filters-list
        :edit-filter="editFilter"
        :filters="allFilters"
      ></filters-list>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import { useDrawer } from '@app/shared/pinia/drawer'
import { SfButtonIcon } from '@app/shared/vue/components/sf-button/index'

import FiltersList from './filters/filters-list'
import ActiveFiltersList from './filters/active-filters-list'
import SegmentInfo from './segments/segment-info'
import SegmentSelector from './segments/segment-selector'

import { SfTime } from '@app/adapters/time'

export default {
  name: 'InsightsLeftPanel',
  components: {
    'filters-list': FiltersList,
    'active-filters-list': ActiveFiltersList,
    'segment-info': SegmentInfo,
    'segment-selector': SegmentSelector,
    'sf-button-icon': SfButtonIcon,
  },

  computed: {
    ...mapGetters([
      'manageTrigger',
      'isSegmentModified',
      'getError',
      'queryTriggers',
      'pendingCompaniesRequest',
      'allCompaniesProcessed',
    ]),

    ...mapState({
      allFilters: (state) => state.filters.allFilters,
      segment: (state) => state.segments.segment,
      isSegmentLoading: (state) => state.segments.isSegmentLoading,
      editedTrigger: (state) => state.triggers.editedTrigger,
      triggerLoading: (state) => state.triggers.triggerLoading,
      activeFilters: (state) => state.filters.activeFilters,
      periodSelection: (state) => state.periodSelection,
      ledgerSelectDate: (state) => state.ledgerSelectDate,
      periodSelectionOpen: (state) => state.periodSelectionOpen,
    }),

    ...mapPiniaState(useDrawer, ['isLeftDrawerOpen']),

    showTriggers() {
      return this.queryTriggers.length
    },

    addTriggerAllowed() {
      // segment loading -> render the button. However, it will be disabled. Prevent "jumps" when switching segments.
      return (
        !this.isSegmentModified &&
        (this.isSegmentLoading || this.segment.period_type)
      )
    },

    today: () =>
      I18n.t(
        'companies_overview.period_selection_new.date_selection_placeholder_with_date',
        { date: SfTime(new Date()).format('DD/MM/YYYY') }
      ),
  },

  watch: {
    periodSelectionOpen(value) {
      if (value) {
        this.openPeriodSelection()
        this.resetPeriodSelectionOpen()
      }
    },
  },

  methods: {
    ...mapPiniaActions(useDrawer, ['closeDrawer']),
    ...mapActions([
      'editFilter',
      'fetchUsersForFirm',
      'fetchFilterDropdownValues',
      'fetchFilteredCompanies',
      'fetchReconciliationTexts',
      'fetchSubmissionDropdownData',
      'loadStateFromUrlQueryParams',
      'resetPeriodSelectionOpen',
      'setLedgerSelectDate',
      'setPeriodSelection',
      'showSegmentModal',
      'setEditedTrigger',
      'fetchSegments',
      'setTriggerRunning',
      'receivedQueryResults',
      'handleFilterOrPeriodChange',
    ]),

    openPeriodSelection() {
      window.modalCitizen.openModal('insights-period-selection', {
        ledgerSelectDate: this.ledgerSelectDate,
        periodSelection: this.periodSelection,
        saveCallback: this.periodSelectionSave,
      })
    },

    async periodSelectionSave(data) {
      this.setLedgerSelectDate(data.ledgerSelectDate)
      this.setPeriodSelection(data.periodSelection)
      const route = await this.handleFilterOrPeriodChange(this.$route)
      this.$router.push(route)
    },

    addTrigger() {
      const trigger = {
        title: null,
        frequency: 'manual',
        trigger_action: {
          action: 'create_remark_on_company',
          payload: {
            todo: false,
            text: I18n.t('insight_triggers.modal.create_remark.template', {
              example: '{{ text.type }}',
            }),
          },
        },
        segment: {
          id: this.segment.id,
        },
      }

      this.setEditedTrigger(trigger)
    },
  },
}
</script>

<style scoped lang="scss">
.rs-filter-list {
  border-radius: 2px;
}
</style>
